import { useOnChange } from "@prismicio/editor-support/React"
import {
  Checkbox,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  FormInput,
  InlineLabel,
} from "@prismicio/editor-ui"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import type { Editor } from "@tiptap/core"
import { useState } from "react"

import type { LinkMarkAttributes } from "../../extensions/Link/Link"
import { createRunCommandsFn } from "../../models/MenuItems"

interface SetWebLinkModalProps {
  open: boolean
  editor: Editor
  setOpen: (open: boolean) => void
}

export function SetWebLinkModal(props: SetWebLinkModalProps) {
  const { open, setOpen } = props

  const { url, setUrl, openInNewTab, setOpenInNewTab, disabled, onUpdate } =
    useLinkModal(props)

  return (
    <Dialog open={open} onOpenChange={setOpen} size="small" noDescription>
      <DialogHeader title="Set Link URL" />

      <DialogContent padding={16}>
        <FormInput
          value={url}
          onValueChange={setUrl}
          placeholder="Paste URL"
          icon="public"
        />
        {url && (
          <InlineLabel value="Open in new tab">
            <Checkbox
              checked={openInNewTab}
              onCheckedChange={setOpenInNewTab}
            />
          </InlineLabel>
        )}
      </DialogContent>

      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton onClick={onUpdate} disabled={disabled}>
          Update
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

function useLinkModal(props: SetWebLinkModalProps) {
  const { editor, open, setOpen } = props

  const selection = getStateFromSelection(editor)

  const [url, setUrl] = useState(selection.url)
  const [openInNewTab, setOpenInNewTab] = useState(selection.openInNewTab)

  useOnChange(open, () => {
    if (!open) return

    setUrl(selection.url)
    setOpenInNewTab(selection.openInNewTab)
  })

  const disabled =
    !url || (url === selection.url && openInNewTab === selection.openInNewTab)

  function onUpdate() {
    const target = openInNewTab ? "_blank" : "_self"

    createRunCommandsFn((commands) =>
      commands
        .extendMarkRange(RichTextNodeType.hyperlink)
        .setWebLink({ href: url, target }),
    )(editor)

    setOpen(false)
  }

  return {
    url,
    setUrl,
    openInNewTab,
    disabled,
    setOpenInNewTab,
    onUpdate,
  }
}

// Note: This must be called before the Dialog steals the focus or the editor won't know what's the current selection.
function getStateFromSelection(editor: Editor) {
  const linkAttributes = editor.getAttributes(
    RichTextNodeType.hyperlink,
  ) as Partial<LinkMarkAttributes>

  return {
    url: linkAttributes.href ?? "",
    openInNewTab: linkAttributes.target === "_blank",
  }
}
